import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import withStore from '../../withStore';
import store from '../../../store';
import { IRootDispatch, RootState } from '../../../store/models';
import analytics from '../../../modules/analytics';
import { getIOSVersion, getIsApp } from '../../../modules/AppInfo';
import { fetchPaymentSettings } from '../../../modules/provider/PaymentSettings';
import ABTest from '../../../modules/abTest';
import { getPrivilegedProvider, PrivilegedProvider } from '../../../api/Providers';
import { useFeatureFlag } from '../../../hooks';
import useAsyncState from '../../../../../packages/hooks/src/useAsyncState';
import stateManager from '../../../modules/user/stateManager';
import YOUR_WORK_ACTIVE from './images/icon-onboarding-your-work-active.svg';
import YOUR_WORK_INACTIVE from './images/icon-onboarding-your-work-inactive.svg';
import LOCATION_ACTIVE from './images/icon-onboarding-location-active.svg';
import LOCATION_INACTIVE from './images/icon-onboarding-location-inactive.svg';
import SERVICES_ACTIVE from './images/icon-onboarding-services-active.svg';
import CALENDAR_ACTIVE from './images/icon-onboarding-calendar-active.svg';
import CONNECT_ACCOUNT_ACTIVE from './images/icon-onboarding-connect-account-active.svg';

import SERVICES_INACTIVE from './images/icon-onboarding-services-inactive.svg';
import CALENDAR_INACTIVE from './images/icon-onboarding-calendar-inactive.svg';
import CONNECT_ACCOUNT_INACTIVE from './images/icon-onboarding-connect-account-inactive.svg';

import OnboardingCarousel from './OnboardingCarousel';
import {
  IAbstractOnboardingCarouselCard,
  IBackEndManagedOnboardingCarouselCard,
  IOnboardingCarouselContainerProps,
  isBackEndManagedCard,
} from './OnboardingCarousel.types';
import { AB_TEST_NAME, AB_TEST_PERCENT } from '../Onboarding/constants';
import useLinkTo from '../../../hooks/useLinkTo';
import {
  useEliteOnboardingCard,
  checkExpireEliteOnboardingCard,
  ELITE_ONBOARDING_SCHEDULE_FLAG_NAME,
  EliteOnboardingCardKey,
} from '../EliteOnboarding';
import { ProviderOnboardingStatus } from '../../../modules/api/providers/onboarding';
import {
  PRO_BUSINESS_INFO_SETTINGS,
  PRO_IMAGE_LIST,
  PRO_MANAGE_ONGOING_SCHEDULE,
  PRO_MANAGE_SERVICES,
} from '../../../route-names';

export const OnboardingCarouselContainer: React.FC<IOnboardingCarouselContainerProps> = ({
  providerId,
  providerName,
  connectAccount = null,
  editAvailability = null,
  editServices = null,
  addGalleryPhoto = null,
  editBusinessInfo = null,
}) => {
  const dispatch = useDispatch<IRootDispatch>();
  const linkTo = useLinkTo();
  const proId: number = typeof providerId === 'number'
    ? providerId
    : parseInt(providerId, 10);

  const [cardDefs, setCardDefs] = useState<IAbstractOnboardingCarouselCard[]>([]);
  const [onboardingComplete, setOnboardingComplete] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const provider = useAsyncState(
    async () => getPrivilegedProvider(proId),
    [proId],
  );

  const paymentConfig = useAsyncState(
    () => fetchPaymentSettings(proId),
    [proId],
  );

  const addGalleryPhotoDefault = () => {
    linkTo(PRO_IMAGE_LIST, {
      providerId,
      edit: 1,
      modalId: 'pro-add-photo',
    });
  };

  const editBusinessInfoDefault = () => {
    linkTo(PRO_BUSINESS_INFO_SETTINGS, { providerId });
  };

  /**
   * AB Test assignment CHECK for full onboarding experience
   * Only checks assignment made in Onboarding.jsx
   */
  const userInOnboardingTest = useAsyncState(async () => {
    const isInTest = await ABTest.isInFeatureTest(
      AB_TEST_NAME,
      AB_TEST_PERCENT,
      { assign: false },
    );
    analytics.track('provider_complete_onboarding_carousel_test_assignment', {
      isInTest,
    });

    return isInTest;
  }, []);

  const connectAccountDefault = () => {
    linkTo('provider.settings_menu.payments', {
      providerId,
      beginOnboarding: true,
      clickSource: 'profile_carousel',
    });
  };

  const editAvailabilityDefault = () => {
    linkTo(PRO_MANAGE_ONGOING_SCHEDULE, { providerId });
  };

  const editServicesDefault = () => {
    linkTo(PRO_MANAGE_SERVICES, { providerId });
  };

  const incompleteCards = (
    cards: IAbstractOnboardingCarouselCard[],
  ) => cards.filter(({ complete }) => !complete);

  const {
    loading: isEliteOnboardingLoading,
    enabled: isEliteOnboardingEnabled,
  } = useFeatureFlag(ELITE_ONBOARDING_SCHEDULE_FLAG_NAME);

  const {
    isLoaded: isEliteOnboardingCardLoaded,
    card: eliteOnboardingCard,
  } = useEliteOnboardingCard();

  const paymentsCard: IBackEndManagedOnboardingCarouselCard = {
    key: 'payments_onboarding',
    title: 'Link your bank account',
    body: 'So clients can book an appointment with you.',
    cta: 'Connect Account',
    onClick: connectAccount || connectAccountDefault,
    incompleteIcon: CONNECT_ACCOUNT_ACTIVE,
    completeIcon: CONNECT_ACCOUNT_INACTIVE,
    complete: false,
  };

  const servicesCard: IBackEndManagedOnboardingCarouselCard = {
    key: 'services',
    title: 'Add Services',
    body: 'So clients know what you specialize in.',
    cta: 'Add Services',
    onClick: editServices || editServicesDefault,
    incompleteIcon: SERVICES_ACTIVE,
    completeIcon: SERVICES_INACTIVE,
    complete: false,
  };

  const availabilityCard: IBackEndManagedOnboardingCarouselCard = {
    key: 'availability',
    title: 'Add availability',
    body: 'So clients know your work schedule.',
    cta: 'Set up Availability',
    onClick: editAvailability || editAvailabilityDefault,
    incompleteIcon: CALENDAR_ACTIVE,
    completeIcon: CALENDAR_INACTIVE,
    complete: false,
  };

  const imagesCard: IBackEndManagedOnboardingCarouselCard = {
    key: 'gallery_image',
    title: 'Add photos\n of your work',
    body: 'To show off your work',
    cta: 'Upload Work',
    onClick: addGalleryPhoto || addGalleryPhotoDefault,
    incompleteIcon: YOUR_WORK_ACTIVE,
    completeIcon: YOUR_WORK_INACTIVE,
    complete: false,
  };

  const locationCard: IBackEndManagedOnboardingCarouselCard = {
    key: 'location',
    title: 'Add location',
    body: 'This helps new clients find professionals in their area.',
    cta: 'Add Business Info',
    onClick: editBusinessInfo || editBusinessInfoDefault,
    incompleteIcon: LOCATION_ACTIVE,
    completeIcon: LOCATION_INACTIVE,
    complete: false,
  };

  const defaultCards: IAbstractOnboardingCarouselCard[] = userInOnboardingTest
    ? [
      paymentsCard,
      imagesCard,
      availabilityCard,
      locationCard,
      servicesCard,
    ]
    : [
      paymentsCard,
      servicesCard,
      availabilityCard,
    ];

  if (!isEliteOnboardingLoading && isEliteOnboardingEnabled) {
    defaultCards.unshift(eliteOnboardingCard);
  }

  const updateProProfileCompleted = (proData: PrivilegedProvider) => {
    const isProProfileCompleted = proData.profile_phase_one_complete_date !== null;
    if (isProProfileCompleted) {
      stateManager.getActionValue('isProProfileCompletedTracked').then(value => {
        if (!value) {
          analytics.track('pro_profile_completed', {
            providerId,
            completedDate: proData.profile_phase_one_complete_date,
          });
          stateManager.addAction('isProProfileCompletedTracked', true);
        }
      });
    }
  };
  useEffect(() => {
    if (providerId && provider && paymentConfig) {
      dispatch.providerOnboarding.fetch(proId);
      updateProProfileCompleted(provider);
    }
  }, [
    providerId,
    provider,
    paymentConfig,
  ]);

  const onboardingStatus = useSelector<RootState, ProviderOnboardingStatus | undefined>(
    store.select.providerOnboarding.status({
      providerId: Number(providerId),
      excludeSubscribeStep: !!(getIOSVersion() && getIsApp()),
    }),
  );

  /**
   * Recalculate cardDefs on updates.
   */
  useEffect(() => {
    const loading = !onboardingStatus
      || !provider
      || !paymentConfig
      || isEliteOnboardingLoading
      || !isEliteOnboardingCardLoaded
      || userInOnboardingTest === undefined;

    if (loading) {
      return;
    }

    let newCardDefs = defaultCards
      .map((card, index) => {
        // Copy the default cards, annotate with complete status and a wrapped
        // onClick handler.
        const complete = isBackEndManagedCard(card)
          ? !onboardingStatus.promptStatus[`prompt_for_${card.key}`]
          : card.complete;
        return {
          ...card,
          complete,
          onClick: () => {
            analytics.track('pro_onboarding_carousel_card_cta_clicked', {
              card_id: card.key,
              zero_index_when_clicked: index,
              already_completed_when_clicked: complete,
              provider_id: providerId,
            });
            return card.onClick();
          },
          onDismiss: card.onDismiss
            ? () => {
              analytics.track('pro_onboarding_carousel_card_dismiss_clicked', {
                card_id: card.key,
                zero_index_when_clicked: index,
                already_completed_when_clicked: complete,
                provider_id: providerId,
              });
              return card.onDismiss();
            }
            : undefined,
        } as IAbstractOnboardingCarouselCard;
      })
      .filter(card => {
        if (card.key === EliteOnboardingCardKey) {
          if (checkExpireEliteOnboardingCard(dispatch, card, provider)) {
            return false;
          }

          return !card.complete;
        }
        // Hide payments onboarding card if incomplete after 3 months.
        // (SaaS pro only)
        if (
          card.key === 'payments_onboarding'
          && !paymentConfig.on_commission_plan
        ) {
          const joinedTime = moment(
            provider.joined_time,
            'YYYY-MM-DDTHH:mm:ss',
          );
          if (moment().diff(joinedTime, 'months') >= 3) {
            return false;
          }
        }
        return true;
      });

    // Move completed items to back of the list
    newCardDefs.sort((a, b) => {
      if (a.complete === b.complete) {
        return 0;
      }
      if (a.complete) {
        return 1;
      }
      return -1;
    });

    const incomplete = incompleteCards(newCardDefs);
    if (
      incomplete.length === 1
      && incomplete[0].key === 'payments_onboarding'
      && !paymentConfig.on_commission_plan
    ) {
      // If the pro is on the SaaS plan, this card is not mandatory and if it
      // is the last card we don't block considering the carousel completed
      newCardDefs = newCardDefs.filter(card => card.key !== 'payments_onboarding');
    }

    const tempOnboardingComplete = !incompleteCards(newCardDefs).length;
    setCardDefs(newCardDefs);
    setInitializing(false);
    setOnboardingComplete(tempOnboardingComplete);
  }, [
    providerId,
    provider,
    paymentConfig,
    userInOnboardingTest,
    onboardingStatus,
    isEliteOnboardingLoading,
    isEliteOnboardingEnabled,
    isEliteOnboardingCardLoaded,
    eliteOnboardingCard.complete,
  ]);

  useEffect(() => {
    const incompleteCount = incompleteCards(cardDefs).length;
    if (incompleteCount) {
      // We're going to render, so track the carousel viewed event
      analytics.track('pro_onboarding_carousel_viewed', {
        cardsIncomplete: incompleteCount,
        providerId,
      });
    }
  }, [incompleteCards(cardDefs).length]);

  if (initializing || onboardingComplete) {
    return null;
  }

  const [firstName] = (providerName || '').split(' ');

  return (
    <OnboardingCarousel
      provider={provider}
      firstName={firstName}
      cards={cardDefs}
    />
  );
};

export const OnboardingCarouselContainerWithStore = withStore(OnboardingCarouselContainer);
